import { Star, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AiRatingStars from './AiRatingStars'
import StatusGainBadge from './StatusGainBadge'
import { RATING_COLORS } from '@/lib/rating-colors'
import type { Achievement } from '@/types'

/**
 * 実績の詳細モーダル（実績一覧・オリジナルトロフィーの材料選択で共用）。
 * onOpenDetail / onDelete を渡したときだけ対応するボタンを表示する。
 * どちらも渡さなければアクション行ごと非表示（材料選択での閲覧専用表示）。
 */
export default function AchievementDetailDialog({
  achievement,
  onClose,
  onOpenDetail,
  onDelete,
}: {
  achievement: Achievement | null
  onClose: () => void
  onOpenDetail?: () => void
  onDelete?: () => void
}) {
  const a = achievement
  return (
    <Dialog open={!!a} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent>
        {a && (
          <>
            <DialogHeader>
              <DialogTitle>{a.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {new Date(a.achieved_at).toLocaleDateString('ja-JP', {
                    year: 'numeric', month: 'long', day: 'numeric',
                  })}
                </span>
                {a.score && <span>· {a.score}</span>}
              </div>

              <div className="flex items-center gap-5">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">自己評価</span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={15}
                        className={i < a.rating ? RATING_COLORS.self : RATING_COLORS.empty}
                      />
                    ))}
                  </div>
                </div>
                {a.is_ai_analyzed && a.ai_rating !== null && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">AI評価</span>
                    <AiRatingStars rating={a.ai_rating} size={15} />
                  </div>
                )}
              </div>

              {a.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {a.tags.map((tag) => (
                    <Badge key={tag} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              )}

              {a.is_ai_analyzed && a.ai_tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {a.ai_tags.slice(0, 4).map((tag) => (
                    <Badge key={tag} className="bg-green-500/10 text-green-700 dark:text-green-400 border-0">{tag}</Badge>
                  ))}
                  {a.ai_tags.length > 4 && (
                    <Badge variant="outline">+{a.ai_tags.length - 4}</Badge>
                  )}
                </div>
              )}

              <StatusGainBadge
                statusGain={a.status_gain}
                earnedPoints={a.earned_points}
                isAiAnalyzed={a.is_ai_analyzed}
              />

              {a.memo && (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted rounded-md p-3">
                  {a.memo}
                </p>
              )}

              {a.open_badges.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">オープンバッジ</p>
                  {a.open_badges.map((badge) => (
                    <div key={badge.id} className="flex items-center gap-3 p-3 border rounded-lg">
                      {badge.badge_image_url && (
                        <img src={badge.badge_image_url} alt={badge.badge_name} className="w-8 h-8 rounded object-contain shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{badge.badge_name}</p>
                        {badge.issuer_name && <p className="text-xs text-muted-foreground truncate">{badge.issuer_name}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(onOpenDetail || onDelete) && (
                <div className="flex gap-2 pt-1">
                  {onOpenDetail && (
                    <Button variant="outline" className="flex-1 gap-1.5" onClick={onOpenDetail}>
                      詳細
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      className="flex-1 gap-1.5 bg-destructive/10 text-destructive hover:bg-destructive/20"
                      onClick={onDelete}
                    >
                      <Trash2 size={15} />
                      削除
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
